import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { GraduationCap, CheckCircle2, LogIn, UserPlus, Mail } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function EnrollWithCoach() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, refreshMe } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [guestMode, setGuestMode] = useState(false);
  const [guest, setGuest] = useState({ email: "", first_name: "", last_name: "" });

  useEffect(() => {
    let cancel = false;
    api.get(`/coach/enroll/${token}`)
      .then((r) => { if (!cancel) setInfo(r.data); })
      .catch((e) => {
        if (cancel) return;
        setError(formatApiError(e.response?.data?.detail) || "Invalid or expired enrollment link.");
      })
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [token]);

  const enroll = async () => {
    if (!user) { goLogin(); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/coach/enroll/${token}`);
      toast.success(data.already ? "You're already enrolled with this coach." : `Enrolled with ${info.coach.name}!`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const goLogin = () => {
    sessionStorage.setItem("post_login_redirect", `/enroll/coach/${token}`);
    navigate("/login");
  };

  const guestEnroll = async (e) => {
    e.preventDefault();
    if (!guest.email || !guest.first_name) { toast.error("Email and first name are required"); return; }
    setBusy(true);
    try {
      await api.post("/auth/quick-register", guest);
      if (refreshMe) await refreshMe();
      await api.post(`/coach/enroll/${token}`);
      toast.success("Account created — you're enrolled! Check your email for sign-in details.");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      if (err.response?.status === 409) {
        toast.error("Email already registered — please sign in instead");
        sessionStorage.setItem("post_login_redirect", `/enroll/coach/${token}`);
        navigate("/login");
      } else {
        toast.error(msg);
      }
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="max-w-xl mx-auto pp-card-elevated p-10 mt-12 animate-pulse">
          <div className="h-6 w-48 bg-white/10 rounded mb-4" />
          <div className="h-4 w-full bg-white/5 rounded" />
        </div>
      </AppLayout>
    );
  }

  if (error || !info) {
    return (
      <AppLayout>
        <div className="max-w-xl mx-auto pp-card-elevated p-10 mt-12 text-center" data-testid="enroll-error">
          <div className="text-[#FF3B30] font-bold mb-2">Couldn't enroll</div>
          <p className="text-zinc-400 text-sm">{error || "Invalid or expired enrollment link."}</p>
          <Link to="/" className="pp-btn-primary inline-block mt-6 px-5 py-2 rounded-lg">Back to home</Link>
        </div>
      </AppLayout>
    );
  }

  const c = info.coach;

  return (
    <AppLayout>
      <div className="max-w-xl mx-auto mt-10" data-testid="enroll-page">
        <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-2">
          <span className="pp-chip flex items-center gap-1"><GraduationCap size={11} /> Coach enrollment</span>
        </div>

        <div className="pp-card-elevated p-6 sm:p-8 flex items-center gap-4">
          <Avatar className="h-16 w-16">
            <AvatarImage src={c.avatar_url || ""} />
            <AvatarFallback className="bg-[#007AFF] text-white text-lg">{(c.name || "?").charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <h1 className="font-heading text-2xl sm:text-3xl font-black" data-testid="enroll-coach-name">{c.name || "Coach"}</h1>
            <div className="text-sm text-zinc-400 mt-0.5">{[c.city, c.country].filter(Boolean).join(", ")}</div>
          </div>
        </div>

        {c.bio && <p className="mt-4 text-sm text-zinc-300">{c.bio}</p>}

        <p className="mt-6 text-sm text-zinc-400">
          By enrolling, your account will be linked to this coach so they can invite you to lessons, clinics, tournaments and more — all in one place.
        </p>

        <div className="mt-6 space-y-3">
          {info.already_enrolled ? (
            <button onClick={() => navigate("/dashboard")} data-testid="enroll-already"
              className="pp-btn-secondary w-full px-5 py-3 rounded-xl flex items-center justify-center gap-2">
              <CheckCircle2 size={16} className="text-[#00E676]" /> You're already enrolled — open dashboard
            </button>
          ) : user ? (
            <button onClick={enroll} disabled={busy} data-testid="enroll-confirm"
              className="pp-btn-primary w-full px-5 py-3 rounded-xl">{busy ? "Enrolling…" : `Enroll with ${c.name}`}</button>
          ) : guestMode ? (
            <form onSubmit={guestEnroll} className="pp-card-elevated p-5 space-y-3" data-testid="enroll-guest-form">
              <div className="text-xs text-zinc-500 uppercase tracking-wider">Continue as guest — we'll create your account in 1 click</div>
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="enroll-guest-first" required placeholder="First name" className="pp-input"
                  value={guest.first_name} onChange={(ev) => setGuest((s) => ({ ...s, first_name: ev.target.value }))} />
                <input data-testid="enroll-guest-last" placeholder="Last name (optional)" className="pp-input"
                  value={guest.last_name} onChange={(ev) => setGuest((s) => ({ ...s, last_name: ev.target.value }))} />
              </div>
              <input data-testid="enroll-guest-email" required type="email" placeholder="you@example.com" className="pp-input"
                value={guest.email} onChange={(ev) => setGuest((s) => ({ ...s, email: ev.target.value }))} />
              <p className="text-xs text-zinc-500"><Mail size={11} className="inline" /> Your sign-in password will be emailed to you.</p>
              <div className="flex gap-2">
                <button type="button" onClick={() => setGuestMode(false)} className="pp-btn-secondary px-4 py-2 rounded-lg text-sm">Back</button>
                <button data-testid="enroll-guest-submit" disabled={busy} className="pp-btn-primary flex-1 px-4 py-2 rounded-lg text-sm">{busy ? "Enrolling…" : "Create account & enroll"}</button>
              </div>
            </form>
          ) : (
            <>
              <button onClick={goLogin} data-testid="enroll-login-cta" disabled={busy}
                className="pp-btn-primary w-full px-5 py-3 rounded-xl flex items-center justify-center gap-2">
                <LogIn size={16} /> Sign in & enroll
              </button>
              <button onClick={() => setGuestMode(true)} data-testid="enroll-guest-cta"
                className="pp-btn-secondary w-full px-5 py-3 rounded-xl flex items-center justify-center gap-2">
                <UserPlus size={16} /> Continue as guest
              </button>
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
